import styled from "@emotion/styled"
import { get } from "lodash"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import tw from "twin.macro"
import linkTransformer from "../utils/linkTransformer"
import tailwindConfig from "../utils/tailwindConfig"
import Container from "./Container"
import Link from "./Link"
import SocialLinks from "./SocialLinks"
import ThemeContext from "../context/theme-context"

const StyledFooter = styled.div`
  ${tw`bg-blue text-white`}
`
const LinksSection = styled.div`
  ${tw`bg-black py-2`}
`

const BottomSection = styled.div`
  ${tw`py-12`}
`

const Grid = styled.div`
  ${tw`flex items-center justify-center flex-wrap -mt-4 -ml-12`}
`

const Column = styled.div`
  ${tw`pl-12 mt-4`}
`

const LinksColumn = styled.div`
  ${tw`mt-4 pl-12 w-full lg:w-3/4`}
`
const SocialColumn = styled.div`
  ${tw`mt-4 pl-12 w-full lg:w-1/4 flex justify-center lg:justify-end`}
  a:hover, a:active {
    ${tw`text-teal`}
  }
`
const LogosGrid = styled.div`
  ${tw`flex items-center flex-wrap -mt-4 -ml-4 lg:-ml-12 justify-center`}
  flex-grow: 1;
`

const LogosColumn = styled(Column)`
  ${tw`w-full`}
`

const LogoColumn = styled.div`
  ${tw`pl-4 lg:pl-12 mt-4 w-auto`}
  img {
    ${tw`bg-black`}
    transition: background-color 300ms ease-in-out;
    width: 60px;
    @media (min-width: ${tailwindConfig.theme.screens.lg}) {
      width: 125px;
    }
  }
  a:hover, a:active {
    img {
      ${tw`bg-teal`}
    }
  }
`

const CopyrightColumn = styled(Column)`
  ${tw`w-full text-center mt-8`}
`

const LinksList = styled.ul`
  ${tw`flex flex-wrap -ml-4 -mt-2 uppercase text-sm justify-center lg:-ml-8 lg:justify-start`}
`

const LinksListItem = styled.li`
  ${tw`pl-4 mt-2 w-auto lg:pl-8`}
  a:hover, a:active {
    ${tw`text-teal`}
  }
`

const Footer = ({ global }) => {
  const links = get(global, "elements.footer_navigation_links.value")
  const logos = get(global, "elements.logos.value")
  const { hydrated: isClient } = useContext(ThemeContext)

  return (
    <StyledFooter>
      <LinksSection>
        <Container>
          <Grid>
            <LinksColumn>
              <LinksList>
                {links.map(link => {
                  const transformedLink = linkTransformer(link)
                  const id = get(link, "system.id")

                  return (
                    <LinksListItem key={id}>
                      <Link to={transformedLink.to}>
                        {transformedLink.label}
                      </Link>
                    </LinksListItem>
                  )
                })}
              </LinksList>
            </LinksColumn>
            <SocialColumn>
              <SocialLinks global={global} />
            </SocialColumn>
          </Grid>
        </Container>
      </LinksSection>
      <BottomSection>
        <Container>
          <Grid>
            <LogosColumn>
              <LogosGrid>
                {logos.map(logo => {
                  const altText = get(logo, "elements.image.value[0].elements.alt_text.value")
                  const logoImage = get(logo, "elements.image.value[0].elements.image.value[0]")
                  const transformedLink = linkTransformer(logo)
                  const id = get(logo, "system.id")
                  const logoMarkup = <img data-src={logoImage.url} alt={altText} className={`${isClient ? 'lazyload' : ''}`} />
                  return (
                    <LogoColumn key={id}>
                      {transformedLink.to ?
                        <Link {...transformedLink}>
                          {logoMarkup}
                        </Link>
                        : logoMarkup
                      }
                      
                    </LogoColumn>
                  )
                })}
              </LogosGrid>
            </LogosColumn>
            <CopyrightColumn>
              © {new Date().getFullYear()} Laitram
            </CopyrightColumn>
          </Grid>
        </Container>
      </BottomSection>
    </StyledFooter>
  )
}

Footer.propTypes = {
  global: PropTypes.object,
}

export default Footer
